import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";

import { WorkerResponse } from "store/types/WorkerResponse";
import Worker from "../../entities/Worker";
import i18n from "../../i18n";
import { apiCallBegan, ApiCallFailedPayload, ApiCallSuccessPayload } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import SliceBase from "../types/SliceBase";
import SliceSingletonBase from "../types/SliceSingletonBase";
import { authHeader, getWorkerId } from "../users/auth";
import BankAccount from "../../entities/BankAccount";
import PersonalInfo from "../../entities/PersonalInfo";
import WorkerDocument from "../../entities/WorkerDocument";
import Language from "../../entities/Language";

// SLICE //
interface State {
    forRequesting: {
        profileImage: {
            src: string;
        } & SliceBase;
        anyWorkedById: {
            data?: Worker | null;
            loading: boolean;
            lastFetch: number | null;
            error: any | null;
        };
    } & SliceSingletonBase<Worker>;
    forPosting: {
        profileImage: {
            src: string;
        } & SliceBase;
        bankAccount: SliceBase;
    } & SliceSingletonBase<Worker>;
    communicationLanguages: SliceSingletonBase<Language[]>;
}

type ClassificatorResponse<T> = T[] | { value: T[] };

const initialState: State = {
    forRequesting: {
        data: null,
        loading: false,
        lastFetch: null,
        error: null,
        profileImage: {
            src: "",
            loading: false,
            lastFetch: null,
            error: null
        },
        anyWorkedById: {
            data: null,
            loading: false,
            lastFetch: null,
            error: null
        }
    },
    forPosting: {
        data: null,
        loading: false,
        lastFetch: null,
        error: null,
        profileImage: {
            src: "",
            loading: false,
            lastFetch: null,
            error: null
        },
        bankAccount: {
            loading: false,
            lastFetch: null,
            error: null
        }
    },
    communicationLanguages: {
        data: null,
        loading: false,
        lastFetch: null,
        error: null
    }
};

const slice = createSlice({
    name: "workerDetails",
    initialState,
    reducers: {
        // REQUESTED WORKER DETAILS
        workerDetailsRequested: workerDetails => {
            workerDetails.forRequesting.loading = true;
            workerDetails.forRequesting.error = null;
        },
        workerDetailsReceived: (
            workerDetails,
            action: PayloadAction<WorkerResponse>
        ) => {
            workerDetails.forRequesting.data = schema(action.payload);
            workerDetails.forRequesting.loading = false;
            workerDetails.forRequesting.lastFetch = Date.now();
        },
        workerDetailsRequestFailed: (
            workerDetails,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            workerDetails.forRequesting.loading = false;
            workerDetails.forRequesting.error = action.payload;
        },

        // REQUESTED PROFILE IMAGE
        profileImageRequested: workerDetails => {
            workerDetails.forRequesting.profileImage.loading = true;
            workerDetails.forRequesting.profileImage.error = null;
        },
        profileImageReceived: (workerDetails, action: PayloadAction<string>) => {
            if (workerDetails.forRequesting.profileImage.src === action.payload) {
                return;
            }

            workerDetails.forRequesting.profileImage.src = action.payload;
            workerDetails.forRequesting.profileImage.loading = false;
            workerDetails.forRequesting.profileImage.lastFetch = Date.now();
        },
        profileImageRequestFailed: (
            workerDetails,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            workerDetails.forRequesting.profileImage.loading = false;
            workerDetails.forRequesting.profileImage.error = action.payload;
        },

        // // REQUESTED COMMUNICATION LANGUAGES
        // communicationLanguagesRequested: workerDetails => {
        //     workerDetails.communicationLanguages.loading = true;
        //     workerDetails.communicationLanguages.error = null;
        // },
        // communicationLanguagesReceived: (
        //     workerDetails,
        //     action: PayloadAction<ClassificatorResponse<Language>>
        // ) => {
        //     workerDetails.communicationLanguages.data = unwrapClassificatorResponse(action.payload);
        //     workerDetails.communicationLanguages.loading = false;
        //     workerDetails.communicationLanguages.lastFetch = Date.now();
        // },
        // communicationLanguagesRequestFailed: (
        //     workerDetails,
        //     action: PayloadAction<ApiCallFailedPayload>
        // ) => {
        //     workerDetails.communicationLanguages.loading = false;
        //     workerDetails.communicationLanguages.error = action.payload;
        // },

        // DELETED PROFILE IMAGE
        profileImageDeleteRequested: workerDetails => {
            workerDetails.forRequesting.profileImage.loading = true;
            workerDetails.forRequesting.profileImage.error = null;
        },
        profileImageDeleted: workerDetails => {
            workerDetails.forRequesting.profileImage.src = "";
            workerDetails.forRequesting.profileImage.loading = false;
        },
        profileImageRequestDeleteFailed: (
            workerDetails,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            workerDetails.forRequesting.profileImage.loading = false;
            workerDetails.forRequesting.profileImage.error = action.payload;
        },
        // ANY WORKER INFO
        anyWorkerDetailsByIdRequested: workerDetails => {
            workerDetails.forRequesting.anyWorkedById.loading = true;
            workerDetails.forRequesting.anyWorkedById.error = null;
        },
        anyWorkerDetailsByIdReceived: (
            workerDetails,
            action: PayloadAction<WorkerResponse>
        ) => {
            workerDetails.forRequesting.anyWorkedById.data = schema(action.payload);
            workerDetails.forRequesting.anyWorkedById.loading = false;
            workerDetails.forRequesting.anyWorkedById.lastFetch = Date.now();
        },
        anyWorkerDetailsByIdFailed: (
            workerDetails,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            workerDetails.forRequesting.anyWorkedById.loading = false;
            workerDetails.forRequesting.anyWorkedById.error = action.payload;
        },
        // POST WORKER NAME CHANGE
        workerNameChangePosted: (
            workerDetails) => {
            workerDetails.forPosting.loading = true;
            workerDetails.forPosting.error = null;
        },
        workerNameChangePostSuccess: (
            workerDetails, action: PayloadAction<ApiCallSuccessPayload>
        ) => {
            workerDetails.forPosting.loading = false;
            workerDetails.forPosting.error = null;
        },
        workerNameChangePostFailed: (
            workerDetails, action: PayloadAction<ApiCallFailedPayload>
        ) => {
            workerDetails.forPosting.loading = false;
            workerDetails.forPosting.error = action.payload;
        },

        // POST WORKER BANK ACCOUNT CHANGE 
        workerBankAccountChangePosted: (
            workerDetails) => {
            workerDetails.forPosting.bankAccount.loading = true;
            workerDetails.forPosting.bankAccount.error = null;
        },
        workerBankAccountChangePostSuccess: (
            workerDetails, action: PayloadAction<ApiCallSuccessPayload>
        ) => {
            workerDetails.forPosting.bankAccount.loading = false;
            workerDetails.forPosting.bankAccount.error = null;
        },
        workerBankAccountChangePostFailed: (
            workerDetails, action: PayloadAction<ApiCallFailedPayload>
        ) => {
            workerDetails.forPosting.bankAccount.loading = false;
            workerDetails.forPosting.bankAccount.error = action.payload;
        },
        // POST WORKER PROFILE IMAGE CHANGE
        profileImageChangePosted: (workerDetails) => {
            workerDetails.forPosting.profileImage.loading = false;
            workerDetails.forPosting.profileImage.error = null;
        },
        profileImageChangePostSuccess: (workerDetails, action: PayloadAction<ApiCallSuccessPayload>) => {
            workerDetails.forPosting.profileImage.loading = false;
            workerDetails.forPosting.profileImage.error = null;
        },
        profileImageChangePostFailed: (
            workerDetails, action: PayloadAction<ApiCallFailedPayload>
        ) => {
            workerDetails.forPosting.profileImage.loading = false;
            workerDetails.forPosting.profileImage.error = action.payload;
        },

        // PUT WORKER PERSONAL INFO CHANGES
        workerPersonalInfoChangePut: (workerDetails) => {
            workerDetails.forPosting.loading = true;
            workerDetails.forPosting.error = null;
        },
        workerPersonalInfoChangePutSuccess: (workerDetails, action: PayloadAction<ApiCallSuccessPayload>) => {
            workerDetails.forPosting.loading = false;
            workerDetails.forPosting.error = null;
        },
        workerPersonalInfoChangePutFailed: (
            workerDetails, action: PayloadAction<ApiCallFailedPayload>
        ) => {
            workerDetails.forPosting.loading = false;
            workerDetails.forPosting.error = action.payload;
        }
    }
});

const {
    workerDetailsRequested,
    workerDetailsReceived,
    workerDetailsRequestFailed,

    profileImageRequested,
    profileImageReceived,
    profileImageRequestFailed,

    // communicationLanguagesRequested,
    // communicationLanguagesReceived,
    // communicationLanguagesRequestFailed,

    profileImageDeleteRequested,
    profileImageDeleted,
    profileImageRequestDeleteFailed,

    anyWorkerDetailsByIdRequested,
    anyWorkerDetailsByIdReceived,
    anyWorkerDetailsByIdFailed,

    workerNameChangePosted,
    workerNameChangePostSuccess,
    workerNameChangePostFailed,

    workerBankAccountChangePosted,
    workerBankAccountChangePostSuccess,
    workerBankAccountChangePostFailed,

    profileImageChangePosted,
    profileImageChangePostSuccess,
    profileImageChangePostFailed,

    workerPersonalInfoChangePut,
    workerPersonalInfoChangePutSuccess,
    workerPersonalInfoChangePutFailed
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
export const getWorkerDetails =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.workerDetails.forRequesting.loading) {
            return;
        }
        const workerId = getWorkerId(getState());
        if (!workerId) {
            return;
        }

        const url = "/api/workers/" + workerId;

        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: { ...authHeader(), "Accept-Language": i18n.language },
                onStart: workerDetailsRequested.type,
                onSuccess: workerDetailsReceived.type,
                onError: workerDetailsRequestFailed.type
            })
        );
    };

export const getWorkerProfileImage =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        const workerId = getWorkerId(getState());
        if (!workerId) return;

        const url = "/api/profile/image/" + workerId;

        const { lastFetch } = getState().entities.workerDetails.forRequesting.profileImage;

        const diffInMinutes = moment().diff(moment(lastFetch), "minutes");
        if (
            diffInMinutes <
            parseInt((window._env_.REACT_APP_COMMON_REQUEST_INTERVAL || process.env.REACT_APP_COMMON_REQUEST_INTERVAL)!, 10)
        ) {
            return;
        }

        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: {
                    ...authHeader(),
                    "Content-Type": "image/jpeg",
                    "Accept-Language": i18n.language
                },
                onStart: profileImageRequested.type,
                onSuccess: profileImageReceived.type,
                onError: profileImageRequestFailed.type,
                responseType: "blob"
            })
        );
    };

// export const getCommunicationLanguages = () => async (dispatch: AppDispatch, getState: () => AppState) => {
//   const { loading } = getState().entities.workerDetails.communicationLanguages;

//   if ( loading ) {
//     return;
//   }

//   const url = `/api/classificator/language`;

//   return await dispatch(
//     apiCallBegan({
//       url,
//       method: "GET",
//       headers: {
//         ...authHeader(),
//         "Accept-Language": i18n.language
//       },
//       onStart: communicationLanguagesRequested.type,
//       onSuccess: communicationLanguagesReceived.type,
//       onError: communicationLanguagesRequestFailed.type
//     })
//   );
// };

export const deleteProfileImage =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        const workerId = getWorkerId(getState());
        const url = "/api/profile/image/" + workerId;

        return await dispatch(
            apiCallBegan({
                url,
                method: "DELETE",
                headers: {
                    ...authHeader(),
                    "Content-Type": "image/jpeg",
                    "Accept-Language": i18n.language
                },
                onStart: profileImageDeleteRequested.type,
                onSuccess: profileImageDeleted.type,
                onError: profileImageRequestDeleteFailed.type
            })
        );
    };

export const updateDisplayedProfileImage =
    (imageURI: string) => (dispatch: AppDispatch) => {
        return dispatch(profileImageReceived(imageURI));
    };

export const getAnyWorkerDetailsById =
    (workerId: number) =>
        async (dispatch: AppDispatch, getState: () => AppState) => {
            if (getState().entities.workerDetails.forRequesting.anyWorkedById.loading) {
                return;
            }
            if (!workerId) {
                return;
            }

            const url = "/api/workers/" + workerId;

            var result = await dispatch(
                apiCallBegan({
                    url,
                    method: "GET",
                    headers: { ...authHeader(), "Accept-Language": i18n.language },
                    onStart: anyWorkerDetailsByIdRequested.type,
                    onSuccess: anyWorkerDetailsByIdReceived.type,
                    onError: anyWorkerDetailsByIdFailed.type
                })
            );
            return result;
        };

export const changeWorkerName =
    (firstName: string, lastName: string) =>
        async (dispatch: AppDispatch, getState: () => AppState) => {
            if (getState().entities.workerDetails.forPosting.loading) {
                return;
            }
            if (!firstName && !lastName) {
                return;
            }
            const workerId = getWorkerId(getState());
            if (!workerId) {
                return;
            }

            dispatch(
                apiCallBegan({
                    url: "/api/Workflow/Name",
                    method: "POST",
                    headers: {
                        ...authHeader(),
                        Accept: "*/*",
                        "Content-Type":
                            "application/json"
                    },
                    data: JSON.stringify({
                        WorkerId: workerId,
                        FirstName: firstName,
                        LastName: lastName
                    }),
                    onStart: workerNameChangePosted.type,
                    onSuccess: workerNameChangePostSuccess.type,
                    onError: workerNameChangePostFailed.type
                })
            );
        };

export const changeWorkerBankAccount =
    (bankAccount: BankAccount) =>
        async (dispatch: AppDispatch, getState: () => AppState) => {
            if (getState().entities.workerDetails.forPosting.bankAccount.loading) {
                return;
            }
            if (!bankAccount) { return; }

            const workerId = getWorkerId(getState());
            if (!workerId) {
                return;
            }

            dispatch(
                apiCallBegan({
                    url: "/api/Workflow/BankAccount",
                    method: "POST",
                    headers: {
                        ...authHeader(),
                        Accept: "*/*",
                        "Content-Type":
                            "application/json"
                    },
                    data: JSON.stringify({
                        workerId: workerId,
                        bankId: bankAccount.BankId,
                        bankName: "",
                        accountNumber: bankAccount.IBAN,
                        accountOwner: bankAccount.OwnerName,
                        effectiveDate: bankAccount.StartDate,
                        percentageOfSalary: bankAccount.PercentageOfSalary,
                        isDefault: bankAccount.IsDefault
                    }),
                    onStart: workerBankAccountChangePosted.type,
                    onSuccess: workerBankAccountChangePostSuccess.type,
                    onError: workerBankAccountChangePostFailed.type
                })
            );
        };

export const setWorkerProfileImage = (image: string) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.workerDetails.forPosting.profileImage.loading) {
        return;
    }
    const workerId = getWorkerId(getState());
    if (!image || !workerId) {
        console.error("Invalid image data or worker ID");
        return;
    }

    try {
        await dispatch(
            apiCallBegan({
                url: `/api/profile/image/${workerId}`,
                method: "POST",
                headers: {
                    ...authHeader(),
                    Accept: "*/*",
                    "Content-Type":
                        "application/json"
                },
                data: JSON.stringify({
                    workerId: workerId,
                    image: image,
                    delete: false
                }),
                onStart: profileImageChangePosted.type,
                onSuccess: profileImageChangePostSuccess.type,
                onError: profileImageChangePostFailed.type
            })
        );
        dispatch(updateDisplayedProfileImage(image));
    } catch (error) {
        console.error("Failed to upload profile image:", error);
    }
};

export const changeWorkerPersonalInfo = (personalInfo: PersonalInfo) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.workerDetails.forPosting.loading) {
        return;
    }

    if (!personalInfo) {
        console.error("Invalid personal info data");
        return;
    }
    const workerId = getWorkerId(getState());
    if (!workerId) {
        return;
    }
    personalInfo.WorkerId = workerId;

    const url = "/api/workers/personalInfo/" + workerId;

    return await dispatch(
        apiCallBegan({
            url,
            method: "PUT",
            headers: {
                ...authHeader(),
                "Content-Type": "application/json",
                "Accept-Language": i18n.language
            },
            data: JSON.stringify({
                ...personalInfo
            }),
            onStart: workerPersonalInfoChangePut.type,
            onSuccess: workerPersonalInfoChangePutSuccess.type,
            onError: workerPersonalInfoChangePutFailed.type
        })
    );
}

// NORMALIZATION //
const schema = (payload: WorkerResponse): Worker | null => {
    if (!payload) {
        return null;
    }

    return {
        BirthDate: payload.BirthDate || "",
        Code: payload.Code || "",
        FirstName: payload.FirstName || "",
        Id: payload.Id,
        Language: payload.Language,
        LastName: payload.LastName || "",
        Name: payload.Name || "",
        SeniorityDate: payload.SeniorityDate,
        SSN: payload.SSN,
        WorkEmail: payload.WorkEmail,
        //TODO: Map properly
        Memberships: payload.Membership,
        PositionName: payload.PositionName,
        Gender: payload.Gender,
        BirthdayVisibleToCoworkers: payload.BirthdayVisibleToCoworkers,
        CommunicationLanguageId: payload.CommunicationLanguageId,
        CitizenshipCountryId: payload.CitizenshipCountryId ?? null,
        BankAccounts: (payload.BankAccounts || []).map(account => ({
            ...account,
            StartDate: account.StartDate || undefined,
            // Optional: handle EndDate
            /* EndDate: (account as any).EndDate ? new Date((account as any).EndDate) : undefined,*/
        } as BankAccount)),
        Documents: (payload.Documents || []).map(document => ({
            Id: document.Id,
            TypeId: document.TypeId,
            TypeName: document.TypeName,
            Value: document.Value,
            EndDate: document.EndDate || undefined
        } as WorkerDocument))
        // WorkAddress: payload.WorkAddress, //TODO: Add to response in BE, as we already have contact info
        // WorkPhone: payload.WorkPhone //TODO: Add to response in BE, as we already have e-mail
    };
};

const unwrapClassificatorResponse = <T>(payload: ClassificatorResponse<T>): T[] => {
    if (Array.isArray(payload)) {
        return payload;
    }

    return payload?.value || [];
};

